import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  FileText,
  Navigation,
} from 'lucide-react'
import { useTrip } from '../trips/useTrip'
import { usePoints } from '../timeline/usePoints'
import {
  useAddPointLive,
  useDocuments,
  usePatchPoint,
  useToday,
} from './useToday'
import { LiveCaptureSheet, type LiveSheetKind } from './LiveCaptureSheet'
import { LiveEmergencyBlock } from './LiveEmergencyBlock'
import { LiveMiniMap } from './LiveMiniMap'
import { TransitPanel } from './TransitPanel'
import { LiveNewPlacePrompt } from './LiveNewPlacePrompt'
import { LiveQuickGrid, type LiveQuickActionId } from './LiveQuickGrid'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { POINT_STATUS_META, POINT_TYPE_META } from '../../shared/lib/pointMeta'
import { gmapsPlaceLink } from '../../shared/lib/gmapsLink'
import { formatDistance, formatWalkEta, haversineMeters } from '../../shared/lib/geoMath'
import { cacheTripSnapshot } from '../../shared/lib/offlineStore'
import { capture } from '../../shared/lib/offlineCaptures'
import { OfflineIndicator } from '../sync/OfflineIndicator'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { listNotes } from '../../shared/api/notes'
import { useOnlineStatus } from '../../shared/hooks/useOnlineStatus'
import { useGeolocation } from '../../shared/hooks/useGeolocation'
import { useNearby } from '../../shared/hooks/useNearby'
import type { Document, TripPoint } from '../../shared/api/types'
import { downloadDocumentFile } from '../../shared/lib/documentFiles'

const MAX_VISIBLE = 5

export function LiveMode() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { t } = useTranslation()
  const trip = useTrip(id)
  const allPoints = usePoints(id)
  const allDocs = useDocuments(id)
  const today = useToday(id)
  const notes = useQuery({queryKey:['notes',tripId],queryFn:()=>listNotes(tripId),enabled:Boolean(id)})
  const patchPoint = usePatchPoint(tripId)
  const addPointLive = useAddPointLive(tripId)
  const photoRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLInputElement>(null)
  const scanRef = useRef<HTMLInputElement>(null)
  const [sheet, setSheet] = useState<LiveSheetKind | null>(null)
  const [busyAction, setBusyAction] = useState<LiveQuickActionId | null>(null)
  const [shareMessage, setShareMessage] = useState<string | null>(null)
  const [unsavedFile, setUnsavedFile] = useState<{ file: File; kind: 'media' | 'document' } | null>(null)
  const [dismissedNewPlace, setDismissedNewPlace] = useState(false)
  const online = useOnlineStatus()
  const geo = useGeolocation(online)
  const nearby = useNearby(tripId, geo.position, online)

  useEffect(() => {
    if (!online || !trip.data || !today.data || !allPoints.data || !allDocs.data) return
    void cacheTripSnapshot({
      tripId: tripId,
      trip: trip.data,
      today: today.data,
      documents: allDocs.data,
      points: allPoints.data,
    })
  }, [online, tripId, trip.data, today.data, allPoints.data, allDocs.data])

  if (today.isLoading || trip.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    )
  }

  if (today.isError || !today.data) {
    return (
      <EmptyState
        title={t('live.load_error')}
        description={t('live.load_error_desc')}
      />
    )
  }

  const { current_point, next_point, today_points, today_documents } = today.data
  const scheduledFocus = next_point ?? current_point ?? today_points[0] ?? null
  const linkPoint =
    nearby.data?.within_radius && nearby.data.nearest_point
      ? nearby.data.nearest_point
      : scheduledFocus
  const focusPoint = linkPoint
  const todayDayIndex = today_points[0]?.day_index ?? 0
  const mapPoint = next_point ?? scheduledFocus

  const distanceToNextM =
    geo.position && next_point?.lat != null && next_point.lng != null
      ? haversineMeters(geo.position.lat, geo.position.lng, next_point.lat, next_point.lng)
      : null

  const showNewPlacePrompt =
    Boolean(nearby.data?.is_new_place && !dismissedNewPlace && geo.position && !nearby.isLoading)

  const mapLabel = geo.position
    ? mapPoint
      ? `You · near ${mapPoint.title}`
      : 'Your location'
    : mapPoint
      ? `Near ${mapPoint.title}${mapPoint.address ? ` · ${mapPoint.address}` : ''}`
      : undefined

  const markVisited = (point: TripPoint) =>
    patchPoint.mutate({ pointId: point.id, patch: { status: 'visited' } })

  const onMediaFile = async (file: File) => {
    try { const result=await capture({tripId,kind:file.type.startsWith('video')?'video':'photo',
      pointId:focusPoint?.id ?? null,blob:file,fileName:file.name})
      setUnsavedFile(null)
      if (file.type.startsWith('video') && videoRef.current) videoRef.current.value = ''
      if (!file.type.startsWith('video') && photoRef.current) photoRef.current.value = ''
      setShareMessage(result.queued ? 'Saved on this device. Upload will retry when online.' : null)
      await qc.invalidateQueries({queryKey:['media',tripId]})
    } catch(err) { setUnsavedFile({file,kind:'media'});setShareMessage(err instanceof Error?err.message:'Could not save photo') }
    finally {setBusyAction(null)}
  }

  const onScanFile = async (file: File) => {
    try { const result=await capture({tripId,kind:'document',pointId:focusPoint?.id ?? null,
      blob:file,fileName:file.name})
      setUnsavedFile(null)
      if (scanRef.current) scanRef.current.value = ''
      setShareMessage(result.queued ? 'Saved on this device. Upload will retry when online.' : null)
      await qc.invalidateQueries({queryKey:['documents',tripId]})
    } catch(err) { setUnsavedFile({file,kind:'document'});setShareMessage(err instanceof Error?err.message:'Could not save document') }
    finally {setBusyAction(null)}
  }

  const saveNote = (text: string) => { void (async () => {
    try { const result=await capture({tripId,kind:'note',pointId:focusPoint?.id ?? null,text})
      setSheet(null)
      setShareMessage(result.queued ? 'Note saved on this device. Upload will retry when online.' : null)
      await qc.invalidateQueries({queryKey:['points',tripId]})
      await qc.invalidateQueries({queryKey:['notes',tripId]})
    } catch(err) { setShareMessage(err instanceof Error?err.message:'Could not save note') }
    finally {setBusyAction(null)}
  })() }

  const savePlace = (title: string, address: string) => {
    addPointLive.mutate(
      {
        title,
        address,
        type: 'free',
        day_index: todayDayIndex,
        lat: geo.position?.lat,
        lng: geo.position?.lng,
      },
      {
        onSuccess: () => {
          setSheet(null)
          setDismissedNewPlace(true)
        },
        onSettled: () => setBusyAction(null),
      },
    )
  }

  async function shareLocation() {
    setShareMessage(null)
    setBusyAction('share_location')
    try {
      if (geo.position) {
        const { lat, lng } = geo.position
        const text = `I'm here: ${lat.toFixed(5)}, ${lng.toFixed(5)}`
        const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`
        if (navigator.share) {
          await navigator.share({ title: trip.data?.title ?? 'My location', text, url: mapsUrl })
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(`${text}\n${mapsUrl}`)
          setShareMessage('Location copied to clipboard.')
        } else {
          setShareMessage(text)
        }
        return
      }

      if (!navigator.geolocation) {
        setShareMessage('Geolocation is not available in this browser.')
        return
      }
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 12_000,
        })
      })
      const { latitude, longitude } = pos.coords
      const text = `I'm here: ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
      const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`
      if (navigator.share) {
        await navigator.share({ title: trip.data?.title ?? 'My location', text, url: mapsUrl })
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${text}\n${mapsUrl}`)
        setShareMessage('Location copied to clipboard.')
      } else {
        setShareMessage(text)
      }
    } catch {
      setShareMessage('Could not get your location. Check permissions and try again.')
    } finally {
      setBusyAction(null)
    }
  }

  function handleQuickAction(action: LiveQuickActionId) {
    setShareMessage(null)
    switch (action) {
      case 'photo':
        setBusyAction('photo')
        photoRef.current?.click()
        break
      case 'video':
        setBusyAction('video')
        videoRef.current?.click()
        break
      case 'voice':
        setSheet('voice')
        break
      case 'note':
        setSheet('note')
        break
      case 'add_place':
        setSheet('add_place')
        break
      case 'scan_doc':
        setBusyAction('scan_doc')
        scanRef.current?.click()
        break
      case 'ai':
        navigate(`/trips/${tripId}/ai`)
        break
      case 'view_photos':
        navigate(`/trips/${tripId}/media`)
        break
      case 'share_location':
        void shareLocation()
        break
      default:
        break
    }
  }

  return (
    <div className="mx-auto max-w-2xl pb-12">
      <Link
        to={`/trips/${tripId}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> {t('live.back_overview')}
      </Link>

      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
          <Navigation className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold text-st-text">{t('live.title')}</h1>
            <span className="inline-flex items-center gap-1 rounded-full bg-st-success/15 px-2 py-0.5 text-xs font-semibold text-st-success">
              <span className="h-1.5 w-1.5 rounded-full bg-st-success" aria-hidden />
              Live
            </span>
          </div>
          <p className="truncate text-sm text-st-muted">{trip.data?.title}</p>
          <p className="text-xs text-st-muted">{localTripDate(trip.data?.time_zone_id)}</p>
          <OfflineIndicator tripId={tripId} />
        </div>
      </div>

      {geo.error && (
        <p className="mb-3 text-sm text-st-muted" role="status">
          {geo.error} — quick actions still work without GPS.
        </p>
      )}

      <div className="mb-5">
        <LiveMiniMap point={mapPoint} userPosition={geo.position} label={mapLabel} />
      </div>
      <TransitPanel tripId={tripId} from={current_point} to={next_point}
        timeZone={trip.data?.time_zone_id} online={online} />

      {showNewPlacePrompt && (
        <LiveNewPlacePrompt
          onAddPlace={() => setSheet('add_place')}
          onDismiss={() => setDismissedNewPlace(true)}
        />
      )}

      {next_point ? (
        <UpNextCard
          tripId={tripId}
          point={next_point}
          distanceM={distanceToNextM}
          onVisited={() => markVisited(next_point)}
          busy={patchPoint.isPending}
        />
      ) : (
        <div className="mb-5 rounded-st-lg border border-st-line bg-st-surface p-5 text-center text-sm text-st-muted">
          {t('live.nothing_left')}
        </div>
      )}

      {current_point && (
        <p className="mb-5 text-sm text-st-muted">
          {t('live.last_visited')}: <span className="text-st-text">{current_point.title}</span>
        </p>
      )}

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold text-st-text">{t('live.today_plan')}</h2>
        {today_points.length === 0 ? (
          <p className="text-sm text-st-muted">{t('live.no_points')}</p>
        ) : (
          <ul className="space-y-2">
            {today_points.slice(0, MAX_VISIBLE).map((p) => (
              <TodayPointRow
                key={p.id}
                tripId={tripId}
                point={p}
                onVisited={() => markVisited(p)}
                busy={patchPoint.isPending}
              />
            ))}
          </ul>
        )}
        {today_points.length > MAX_VISIBLE && (
          <Link
            to={`/trips/${tripId}/timeline`}
            className="mt-2 inline-block text-sm text-st-primary hover:underline"
          >
            {t('live.see_all', { count: today_points.length })}
          </Link>
        )}
      </section>

      {(notes.data?.length ?? 0)>0 && <section className="mb-6"><h2 className="mb-2 text-sm font-semibold">Notes</h2>
        <ul className="space-y-2">{notes.data!.slice(0,5).map(n=><li key={n.id} className="rounded-st-md border border-st-line bg-st-surface p-3 text-sm whitespace-pre-wrap">{n.text}</li>)}</ul>
      </section>}

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold text-st-text">{t('live.today_tickets')}</h2>
        <TodayDocumentsScroll documents={today_documents} noDocsText={t('live.no_docs')} />
      </section>

      <div className="mb-4">
        <LiveQuickGrid
          onAction={handleQuickAction}
          attachDisabled={!focusPoint}
          busyAction={busyAction}
        />
        {shareMessage && <p className="mt-2 text-sm text-st-muted">{shareMessage}</p>}
        {unsavedFile && <div role="alert" className="mt-2 rounded border border-st-danger p-3 text-sm">
          <p>{t('live.storage_full')}: {unsavedFile.file.name}</p>
          <Button type="button" variant="outline" onClick={() => void (unsavedFile.kind === 'document'
            ? onScanFile(unsavedFile.file) : onMediaFile(unsavedFile.file))}>{t('live.retry_save')}</Button>
        </div>}
      </div>

      <LiveEmergencyBlock position={geo.position} />

      <input
        ref={photoRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void onMediaFile(file)
          else setBusyAction(null)
        }}
      />
      <input
        ref={videoRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void onMediaFile(file)
          else setBusyAction(null)
        }}
      />
      <input
        ref={scanRef}
        type="file"
        accept="image/*,.pdf,application/pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void onScanFile(file)
          else setBusyAction(null)
        }}
      />

      <LiveCaptureSheet
        kind={sheet}
        tripId={tripId}
        focusPointId={linkPoint?.id ?? null}
        focusPointTitle={focusPoint?.title}
        busy={patchPoint.isPending || addPointLive.isPending}
        onClose={() => setSheet(null)}
        onSaveNote={saveNote}
        onAddPlace={savePlace}
      />
    </div>
  )
}

function localTripDate(zone?: string): string {
  try { return new Intl.DateTimeFormat(undefined,{timeZone:zone ?? 'Etc/UTC',weekday:'long',year:'numeric',month:'long',day:'numeric'}).format(new Date()) }
  catch { return new Intl.DateTimeFormat(undefined,{timeZone:'Etc/UTC',weekday:'long',year:'numeric',month:'long',day:'numeric'}).format(new Date()) }
}

function TodayDocumentsScroll({ documents, noDocsText }: { documents: Document[]; noDocsText: string }) {
  const { t } = useTranslation()
  const [fileError, setFileError] = useState('')
  if (documents.length === 0) {
    return <p className="text-sm text-st-muted">{noDocsText}</p>
  }

  return (
    <>
    {fileError && <p role="alert" className="text-sm text-st-danger">{fileError}</p>}
    <ul className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      {documents.map((d) => (
        <li
          key={d.id}
          className="flex w-36 shrink-0 flex-col gap-1 rounded-st-md border border-st-line bg-st-surface p-3"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-st-md bg-st-surface-2 text-st-primary">
            <FileText className="h-4 w-4" />
          </span>
          <p className="truncate text-sm font-medium capitalize text-st-text">{d.type}</p>
          <p className="truncate text-xs text-st-muted">{d.file_url.split('/').pop()}</p>
          <button type="button" className="text-start text-xs text-st-primary underline"
            onClick={() => { void downloadDocumentFile(d).catch(() => setFileError(t('files.unavailable'))) }}>
            {t('files.open')}</button>
        </li>
      ))}
    </ul>
    </>
  )
}

function UpNextCard({
  tripId,
  point,
  distanceM,
  onVisited,
  busy,
}: {
  tripId: string
  point: TripPoint
  distanceM: number | null
  onVisited: () => void
  busy: boolean
}) {
  const { t } = useTranslation()
  const { Icon, label } = POINT_TYPE_META[point.type]
  return (
    <div className="mb-5 rounded-st-lg border border-st-primary/30 bg-st-primary/5 p-5">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-st-primary">{t('live.up_next')}</p>
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-st-md bg-st-primary/15 text-st-primary">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-xl font-bold text-st-text">{point.title}</h3>
          <p className="truncate text-sm text-st-muted">
            {label}
            {point.address ? ` · ${point.address}` : ''}
            {point.time ? ` · ${point.time}` : ''}
          </p>
          {distanceM != null && (
            <p className="mt-1 text-sm font-medium text-st-primary">
              {formatDistance(distanceM)} · {t('live.walk_eta', { eta: formatWalkEta(distanceM) })}
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={onVisited} disabled={busy}>
          <CheckCircle2 className="h-4 w-4" /> {t('live.mark_visited')}
        </Button>
        <Link
          to={`/trips/${tripId}/points/${point.id}`}
          className="inline-flex min-h-11 items-center gap-2 rounded-st-md border border-st-line bg-st-surface px-4 text-sm font-medium text-st-text hover:bg-st-surface-2"
        >
          {t('common.details')}
        </Link>
        <a
          href={gmapsPlaceLink(point.lat, point.lng, `${point.title} ${point.address}`)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-11 items-center gap-2 rounded-st-md border border-st-line bg-st-surface px-4 text-sm font-medium text-st-text hover:bg-st-surface-2"
        >
          <ExternalLink className="h-4 w-4" /> {t('common.navigate')}
        </a>
      </div>
    </div>
  )
}

function TodayPointRow({
  tripId,
  point,
  onVisited,
  busy,
}: {
  tripId: string
  point: TripPoint
  onVisited: () => void
  busy: boolean
}) {
  const { t } = useTranslation()
  const { Icon, label } = POINT_TYPE_META[point.type]
  const status = POINT_STATUS_META[point.status]
  return (
    <li className="flex items-center gap-3 rounded-st-md border border-st-line bg-st-surface p-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-st-md bg-st-surface-2 text-st-primary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/trips/${tripId}/points/${point.id}`}
            className="truncate text-sm font-medium text-st-text hover:text-st-primary hover:underline"
          >
            {point.title}
          </Link>
          <span className="text-xs" style={{ color: status.color }} title={status.label}>
            {status.symbol}
          </span>
        </div>
        <p className="truncate text-xs text-st-muted">{label}</p>
      </div>
      {point.status !== 'visited' && (
        <button
          type="button"
          onClick={onVisited}
          disabled={busy}
          className="rounded-st-sm p-1.5 text-st-muted hover:bg-st-success/10 hover:text-st-success disabled:opacity-50"
          title={t('live.mark_visited')}
          aria-label={`${t('live.mark_visited')}: ${point.title}`}
        >
          <CheckCircle2 className="h-4 w-4" />
        </button>
      )}
    </li>
  )
}
